import { toast } from "sonner";
import { formatIDR, publicFileUrl } from "./api";

export function copyText(text, successMsg = "Copied to clipboard") {
    const done = () => toast.success(successMsg);
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
    } else {
        fallbackCopy(text, done);
    }
}

function fallbackCopy(text, done) {
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        done();
    } catch {
        toast.error("Copy failed");
    }
}

/** Public, customer-safe product URL built from the current origin. */
export function productLink(product) {
    const brand = product?.brand || "hoshizora";
    return `${window.location.origin}/${brand}/${product.id}`;
}

/** Name / Price / "View Product:" / public URL (URL appears exactly once). */
function buildShareText(product) {
    const lines = [product.name];
    if (product.retail_price) lines.push(formatIDR(product.retail_price));
    lines.push("", "View Product:", productLink(product));
    return lines.join("\n");
}

/** Fetch an image path as a File ready for navigator.share. Uses the public
 *  URL so this works both for signed-in and signed-out viewers. Returns null
 *  on any CORS/network failure so callers can fall back to text sharing. */
async function fetchImageAsFile(path, nameHint) {
    if (!path) return null;
    try {
        const res = await fetch(publicFileUrl(path), { mode: "cors" });
        if (!res.ok) return null;
        const blob = await res.blob();
        const ext = (blob.type.split("/")[1] || "jpg").split("+")[0];
        const safeName = (nameHint || "product").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
        return new File([blob], `${safeName}.${ext}`, { type: blob.type });
    } catch {
        return null;
    }
}

/** Convenience: resolve a specific image (by index) to a File. Used by
 *  Product Detail so Admin Sales can share the exact image they're viewing. */
export async function imageAtIndexAsFile(product, index) {
    const img = product?.images?.[index];
    if (!img) return null;
    return fetchImageAsFile(img.path, product.name);
}

/**
 * Primary: share the product image as a file with Name / Price / View Product URL.
 * Fallback (no file sharing): same text only. The URL is only in `text` so it never duplicates.
 */
export async function shareProduct(product, { imageOverride = null } = {}) {
    const title = product.name;
    const text = buildShareText(product);

    if (navigator.share) {
        const main = product?.images?.find((i) => i.is_main) || product?.images?.[0];
        const file = imageOverride || (main ? await fetchImageAsFile(main.path, product.name) : null);
        if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
                await navigator.share({ title, text, files: [file] });
                return "native-with-image";
            } catch (ex) {
                if (ex?.name === "AbortError") return "cancelled";
                /* fall through to text share */
            }
        }
        try {
            await navigator.share({ title, text });
            return "native";
        } catch (ex) {
            if (ex?.name === "AbortError") return "cancelled";
        }
    }
    // Legacy fallback — copy the same share text.
    copyText(text, "Product details copied");
    return "copied";
}

export function timeAgo(iso) {
    if (!iso) return "Never synced";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "—";
    const diff = Math.max(0, Date.now() - then);
    const s = Math.floor(diff / 1000);
    if (s < 60) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hr ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d} d ago`;
    return new Date(iso).toLocaleDateString("en-US");
}
